import { ACTIVE_ITEMS } from '@constants/activeItems';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel } from '@mui/material';
import { RootContext } from 'components/RootContainer';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { LIMIT_ITEMS_SELECT } from '../../../../@constants/Pagination';
import { ChargeCodeModel } from '../../../../models/Settings/RevenuManagement/ChargeCodeModel';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 10;

export const ChargeCodeField: FC<{
  value: number[] | null;
  onChange: (value: number[] | null) => void;
  filterFunction?: (item: ChargeCodeModel) => boolean;
}> = ({ value, onChange, filterFunction }) => {
  const { t } = useTranslation('reservation');

  const {
    HotelApi: {
      settings: {
        revenuManagement: { chargeCode },
      },
    },
  } = useContext(RootContext);

  const { isLoading, data } = useQuery({
    queryKey: ['get-charge-codes'],
    queryFn: () => chargeCode.getAll({ search: [ACTIVE_ITEMS], limit: LIMIT_ITEMS_SELECT }),
  });

  const handleChange = (codeId: number | 'all') => {
    if (!data) return;

    const currentValue = value || [];

    if (codeId === 'all') {
      const allIds = data?.map(item => item.id!);
      // If all items are currently selected, clear selection, otherwise select all
      onChange(currentValue.length === allIds.length ? [] : allIds);
      return;
    }

    const newValue = currentValue.includes(codeId)
      ? currentValue.filter(id => id !== codeId)
      : [...currentValue, codeId];
    onChange(newValue);
  };

  if (isLoading) {
    return <>...</>;
  }

  if (!data) {
    return <>...</>;
  }

  const currentValue = value || [];
  const allSelected = currentValue.length === data.length && data.length > 0;
  const hasSelection = currentValue.length > 0;
  const indeterminate = hasSelection && !allSelected;

  return (
    <Box
      sx={{
        boxShadow: 'inset 0 -1px 0 0 #d2d7de',
        pb: 2,
      }}
    >
      <FormControl variant="standard" fullWidth>
        <InputLabel shrink>Charge Codes</InputLabel>
        <Box
          sx={{
            mt: 3,
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            overflow: 'auto',
          }}
        >
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={() => handleChange('all')}
                  sx={{ mr: 1 }}
                />
              }
              label={t('list.filter.all')}
              sx={{ py: 0.5, pl: 1.5 }}
            />
            {(data || [])
              .filter(item => {
                if (filterFunction) {
                  return filterFunction(item);
                }
                return true;
              })
              ?.map(code => (
                <FormControlLabel
                  key={code.id}
                  control={
                    <Checkbox
                      checked={currentValue.includes(code?.id || 0)}
                      onChange={() => handleChange(code?.id || 0)}
                      sx={{ mr: 1 }}
                    />
                  }
                  label={code.name}
                  sx={{ py: 0.5, pl: 1.5 }}
                />
              ))}
          </FormGroup>
        </Box>
      </FormControl>
    </Box>
  );
};
